<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import InvoicesAPI from 'dashboard/api/invoices';
import InvoiceCard from './InvoiceCard.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();

const invoices = ref([]);
const isLoading = ref(false);

const fetchInvoices = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  try {
    const response = await InvoicesAPI.getForContact(props.contactId);
    invoices.value = response.data.payload || [];
  } catch (error) {
    // Non-critical: the panel simply stays empty; actions surface their own errors.
  } finally {
    isLoading.value = false;
  }
};

const replaceInvoice = updated => {
  invoices.value = invoices.value.map(invoice =>
    invoice.id === updated.id ? updated : invoice
  );
};

const actionError = error =>
  useAlert(error?.response?.data?.error || t('INVOICES.ACTIONS.ERROR'));

const onVerify = async invoice => {
  try {
    const response = await InvoicesAPI.verify(invoice.id);
    const { verdict, ...updated } = response.data;
    replaceInvoice(updated);
    if (verdict === 'paid') {
      useAlert(t('INVOICES.ACTIONS.VERIFY_PAID'));
    } else {
      useAlert(
        t('INVOICES.ACTIONS.VERIFY_RESULT', { status: verdict || 'pending' })
      );
    }
  } catch (error) {
    actionError(error);
  }
};

const onVoid = async invoice => {
  try {
    const response = await InvoicesAPI.void(invoice.id);
    replaceInvoice(response.data);
    useAlert(t('INVOICES.ACTIONS.VOID_SUCCESS'));
  } catch (error) {
    actionError(error);
  }
};

const onNewLink = async invoice => {
  try {
    const response = await InvoicesAPI.createPayment(invoice.id, 'khalti');
    replaceInvoice(response.data);
    useAlert(t('INVOICES.ACTIONS.NEW_LINK_SUCCESS'));
  } catch (error) {
    actionError(error);
  }
};

const refundDialogRef = ref(null);
const refundTarget = ref(null);
const refundMobile = ref('');
const isRefunding = ref(false);

const refundContext = computed(() => {
  if (!refundTarget.value) return {};
  const invoice = refundTarget.value;
  const paidPayment = (invoice.payments || []).find(
    payment => payment.status === 'paid'
  );
  const provider = paidPayment?.provider || 'khalti';
  return {
    number: invoice.number,
    amount: `${invoice.currency} ${(invoice.total_cents / 100).toFixed(2)}`,
    provider: provider.charAt(0).toUpperCase() + provider.slice(1),
  };
});

const refundTitle = computed(() =>
  refundTarget.value ? t('INVOICES.REFUND.TITLE', refundContext.value) : ''
);
const refundDescription = computed(() =>
  refundTarget.value ? t('INVOICES.REFUND.DESC', refundContext.value) : ''
);

const openRefundModal = invoice => {
  refundTarget.value = invoice;
  refundMobile.value = '';
  refundDialogRef.value?.open();
};

const closeRefundModal = () => {
  refundTarget.value = null;
};

const confirmRefund = async () => {
  if (!refundTarget.value || isRefunding.value) return;

  isRefunding.value = true;
  try {
    const response = await InvoicesAPI.refund(
      refundTarget.value.id,
      refundMobile.value.trim() || undefined
    );
    replaceInvoice(response.data);
    useAlert(t('INVOICES.REFUND.SUCCESS'));
    refundDialogRef.value?.close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INVOICES.REFUND.ERROR'));
  } finally {
    isRefunding.value = false;
  }
};

watch(() => props.contactId, fetchInvoices);

onMounted(() => {
  fetchInvoices();
  emitter.on('payments.invoiceCreated', fetchInvoices);
});

onBeforeUnmount(() => {
  emitter.off('payments.invoiceCreated', fetchInvoices);
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex justify-end">
      <NextButton
        v-tooltip.top-end="$t('INVOICES.LIST.REFRESH')"
        icon="i-lucide-refresh-cw"
        slate
        ghost
        xs
        :is-loading="isLoading"
        @click="fetchInvoices"
      />
    </div>
    <div v-if="isLoading && !invoices.length" class="flex justify-center py-4">
      <Spinner />
    </div>
    <p
      v-else-if="!invoices.length"
      class="py-2 text-sm text-center text-n-slate-11"
    >
      {{ $t('INVOICES.LIST.EMPTY') }}
    </p>
    <InvoiceCard
      v-for="invoice in invoices"
      :key="invoice.id"
      :invoice="invoice"
      @verify="onVerify(invoice)"
      @void="onVoid(invoice)"
      @new-link="onNewLink(invoice)"
      @refund="openRefundModal(invoice)"
    />

    <Dialog
      ref="refundDialogRef"
      type="alert"
      :title="refundTitle"
      :description="refundDescription"
      :confirm-button-label="$t('INVOICES.REFUND.CONFIRM')"
      :cancel-button-label="$t('INVOICES.REFUND.CANCEL')"
      :is-loading="isRefunding"
      @confirm="confirmRefund"
      @close="closeRefundModal"
    >
      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INVOICES.REFUND.MOBILE_LABEL') }}
        </span>
        <input
          v-model="refundMobile"
          type="text"
          inputmode="tel"
          class="reset-base w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
        <span class="text-xs text-n-slate-11">
          {{ $t('INVOICES.REFUND.MOBILE_HELP') }}
        </span>
      </label>
    </Dialog>
  </div>
</template>
